import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { HelpCircle, X, Smartphone, Monitor, Copy, Check } from 'lucide-react';
import { Button } from './ui/button';

const URLGuide = ({ language }) => {
  const [isOpen, setIsOpen] = useState(false);

  const steps = language === 'en' 
    ? [
        {
          icon: Monitor,
          title: "Step 1: Find Your Product",
          desc: "Browse Amazon, SHEIN, Nike, or any online store and find the product you want"
        },
        {
          icon: Smartphone,
          title: "Step 2: Look at the Top",
          desc: "Look at the address bar at the top of your browser (where it shows the website address)"
        },
        {
          icon: Copy,
          title: "Step 3: Copy the Link",
          desc: "Tap or click on the address bar, select all the text, and copy it"
        },
        {
          icon: Check,
          title: "Step 4: Paste Here",
          desc: "Paste the copied link into the 'Product URL' field in our form"
        }
      ]
    : [
        {
          icon: Monitor,
          title: "Paso 1: Encuentra tu Producto",
          desc: "Busca en Amazon, SHEIN, Nike o cualquier tienda en línea y encuentra el producto que deseas"
        },
        {
          icon: Smartphone,
          title: "Paso 2: Mira Arriba",
          desc: "Mira la barra de direcciones en la parte superior de tu navegador (donde muestra la dirección del sitio web)"
        },
        {
          icon: Copy,
          title: "Paso 3: Copia el Enlace",
          desc: "Toca o haz clic en la barra de direcciones, selecciona todo el texto y cópialo"
        },
        {
          icon: Check,
          title: "Paso 4: Pega Aquí",
          desc: "Pega el enlace copiado en el campo 'URL del Producto' en nuestro formulario"
        }
      ];

  return (
    <div className="relative">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 text-[#8b1538] hover:text-[#6b0f2a] hover:bg-[#8b1538]/10"
      >
        <HelpCircle className="w-4 h-4" />
        {language === 'en' ? 'How to copy a product URL?' : '¿Cómo copiar una URL de producto?'}
      </Button>

      {isOpen && (
        <Card className="absolute z-50 w-full md:w-[600px] top-full mt-2 border-2 border-[#8b1538]/30 shadow-2xl">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-[#1e1919]">
                {language === 'en' ? 'How to Copy Product URL' : 'Cómo Copiar URL del Producto'}
              </h3>
              <button
                onClick={() => setIsOpen(false)}
                className="text-[#736c64] hover:text-[#1e1919]"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-6">
              {steps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <div key={index} className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-full bg-[#fad24b]/20 flex items-center justify-center">
                        <Icon className="w-6 h-6 text-[#8b1538]" />
                      </div>
                    </div>
                    <div>
                      <h4 className="font-semibold text-[#1e1919] mb-1">{step.title}</h4>
                      <p className="text-sm text-[#736c64]">{step.desc}</p>
                    </div>
                  </div>
                );
              })}

              {/* Visual Example */}
              <div className="mt-6 p-4 bg-[#f7f5f2] rounded-lg border-2 border-[#3dd3ee]/30">
                <p className="text-sm font-semibold text-[#1e1919] mb-3">
                  {language === 'en' ? 'Example of Product URL:' : 'Ejemplo de URL del Producto:'}
                </p>
                <div className="bg-white p-3 rounded border border-[#736c64]/30 font-mono text-xs break-all text-[#8b1538]">
                  https://www.amazon.com/Nike-Air-Force-Shoes/dp/B08XYZ123
                </div>
                <p className="text-xs text-[#736c64] mt-2">
                  {language === 'en' 
                    ? '✓ This is what the URL looks like in your browser address bar' 
                    : '✓ Así se ve la URL en la barra de direcciones de tu navegador'}
                </p>
              </div>

              {/* Mobile Instructions */}
              <div className="mt-4 p-4 bg-[#ff8c19]/10 rounded-lg">
                <p className="text-sm font-semibold text-[#1e1919] mb-2">
                  📱 {language === 'en' ? 'On Mobile:' : 'En Móvil:'}
                </p>
                <p className="text-xs text-[#736c64]">
                  {language === 'en'
                    ? 'Tap the address bar at the top → Tap "Copy" or long-press and select "Copy"'
                    : 'Toca la barra de direcciones arriba → Toca "Copiar" o mantén presionado y selecciona "Copiar"'}
                </p>
              </div>

              {/* Desktop Instructions */}
              <div className="p-4 bg-[#3dd3ee]/10 rounded-lg">
                <p className="text-sm font-semibold text-[#1e1919] mb-2">
                  💻 {language === 'en' ? 'On Computer:' : 'En Computadora:'}
                </p>
                <p className="text-xs text-[#736c64]">
                  {language === 'en'
                    ? 'Click the address bar → Press Ctrl+A (or Cmd+A on Mac) → Press Ctrl+C (or Cmd+C on Mac)'
                    : 'Haz clic en la barra de direcciones → Presiona Ctrl+A (o Cmd+A en Mac) → Presiona Ctrl+C (o Cmd+C en Mac)'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default URLGuide;
